import { Box, chakra, Flex, Text, useToast } from "@chakra-ui/react";
import { RiDraggable } from "@remixicon/react";
import { CellContext, Row } from "@tanstack/react-table";
import { useId } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useParams } from "react-router";

import { ErrorResponseDisplay } from "@/common/components/ErrorResponseDisplay";
import { TreeVisualization } from "@/common/components/TreeVisualization";
import { DragType } from "@/common/types/dragDropTypes";
import { IndentType } from "@/common/utils/tree";
import {
  FeatureDisplay,
  FeatureIndicatorsDisplay,
} from "@/feature/components/FeatureDisplay";
import RowExpanderButton from "@/feature/components/RowExpanderButton";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { FlagWithSubRows } from "@/feature/data/useFlags";
import { getFeatureParentNames } from "@reflag/shared/utils/getFeatureParentNames";

const DraggableIcon = chakra(RiDraggable, {
  baseStyle: {
    boxSize: 3,
    color: "dimmed",
  },
});

// A simplified type for drag/drop that matches what we need from FeatureName
type FlagForDragDrop = {
  id: string;
  name: string;
  parentFeatureId: string | null;
};

export function FlagNameCell({
  row,
  table,
}: CellContext<FlagWithSubRows, string>) {
  const isGroupedRow = row.getIsGrouped();
  const featureDisplayId = useId();

  const toast = useToast();
  const { companyId } = useParams();
  const featureUpdateMutation = useFeatureUpdateMutation(companyId!);

  const { data: featureNames = [] } = useFeatureNamesData();
  const featureMetadata = featureNames.find(
    (f) => f.id === row.original.featureId,
  );

  // Convert FlagHeaderDTO to the format expected by FeatureDisplay
  const featureForDisplay = {
    id: row.original.featureId,
    name: row.original.name,
    key: row.original.key,
    icon: featureMetadata?.icon ?? null,
    iconColor: featureMetadata?.iconColor ?? null,
    source: featureMetadata?.source ?? ("event" as const),
    isArchived: row.original.archived,
    isPermanent: row.original.permanent,
    isStale: row.original.stale,
    parentFeatureId: row.original.parentFlagId ?? null,
  };

  const [{ canDrag }, drag, preview] = useDrag<
    FlagForDragDrop,
    void,
    { canDrag: boolean }
  >(
    () => ({
      type: DragType.FeatureDisplay,
      item: () => ({
        id: row.original.featureId,
        name: row.original.name,
        parentFeatureId: row.original.parentFlagId ?? null,
      }),
      collect(monitor) {
        return {
          canDrag: monitor.canDrag(),
        };
      },
    }),
    [row.original],
  );

  const [{ isOver, canDrop }, drop] = useDrop<
    FlagForDragDrop,
    void,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: DragType.FeatureDisplay,
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        };
      },
      canDrop(feature) {
        const dropFeatureId = row.original.featureId;

        // Don't attempt to re-set the current parent flag, or assign self as parent
        if ([feature.id, feature.parentFeatureId].includes(dropFeatureId)) {
          return false;
        }

        // Can't drop a flag on its own children
        const dropParents = getFeatureParentNames(featureNames, dropFeatureId);
        if (dropParents.some((p) => p.id === feature.id)) {
          return false;
        }

        return true;
      },
      drop(feature) {
        featureUpdateMutation.mutate(
          {
            id: feature.id,
            parentFeatureId: row.original.featureId,
          },
          {
            onSuccess() {
              toast({
                title: `Flag "${feature.name}" is now a child of "${row.original.name}"`,
                status: "success",
              });
            },
            onError(error) {
              toast({
                title: (
                  <Box>
                    <Text>{`Unable to move "${feature.name}" inside "${row.original.name}".`}</Text>
                    <ErrorResponseDisplay response={error.response?.data} />
                  </Box>
                ),
                status: "error",
              });
            },
          },
        );
      },
    }),
    [row.original, featureNames, featureUpdateMutation, toast],
  );

  const sortType = (table.options?.meta as any)?.sortType;
  if (sortType !== "hierarchical") {
    return (
      <Flex alignItems="center" gap="2">
        <FeatureDisplay
          feature={featureForDisplay}
          flex="0 1 auto"
          id={featureDisplayId}
          link
          showArchivedLabel
          onClick={(e) => e.stopPropagation()}
        />

        <FeatureIndicatorsDisplay
          feature={featureForDisplay}
          showAutoFeedbackSurveyIndicator={false}
          showStaleIndicator
        />
      </Flex>
    );
  }

  preview(getEmptyImage());

  // When the flag is a child in a tree, but is displayed as a root-node
  // because its parents are not a part of the same view; show the
  // parents names as well
  const shouldShowParentNames =
    row.original.parentFlagId !== undefined && row.getParentRow() === undefined;

  return (
    <Flex
      ref={drop}
      data-candrop={isOver && canDrop}
      flex="0 1 auto"
      ml={-6}
      my={-3.5}
      pl={6}
      position="relative"
      py={3}
      transition={"all 200ms ease-in-out"}
    >
      {/* Droppable indicator */}
      {canDrop && (
        <Box
          aria-hidden="true"
          background="currentColor"
          borderRadius="2px"
          h="10px"
          left={2}
          opacity={isOver ? "0.7" : "0.1"}
          pointerEvents="none"
          position="absolute"
          top="50%"
          transform="translateY(-50%)"
          transition="opacity 200ms"
          w="3px"
        ></Box>
      )}

      {/* Drag handle */}
      <Box
        ref={drag}
        background="appBackground"
        border="1px solid"
        borderColor="appBorder"
        cursor={canDrag ? "grab" : "pointer"}
        left={1}
        opacity="0"
        position="absolute"
        py={1}
        rounded="md"
        sx={{
          "&:active": {
            cursor: canDrag ? "grabbing" : "pointer",
          },
        }}
        top="50%"
        transform="translateY(-50%)"
        transition="opacity 150ms ease-in-out"
        zIndex="1"
        aria-hidden
        data-draghandle
      >
        <DraggableIcon />
      </Box>

      <TreeVisualization
        indents={getTreeVisualizationTypesFromTableRow(row, isGroupedRow)}
        my={-5}
      />

      <FeatureDisplay
        feature={featureForDisplay}
        flex="0 1 auto"
        id={featureDisplayId}
        includeParents={shouldShowParentNames}
        showArchivedLabel={false}
        link
        onClick={(e) => e.stopPropagation()}
      />

      {/* Children expansion indicator */}
      {row.getCanExpand() ? (
        <RowExpanderButton
          count={row.getLeafRows().length}
          isExpanded={row.getIsExpanded()}
          mx={2}
          onToggle={(e) => {
            e.stopPropagation();
            row.getToggleExpandedHandler()();
          }}
        />
      ) : null}

      <Box flexGrow="1" minW={4}></Box>

      <FeatureIndicatorsDisplay
        feature={featureForDisplay}
        showAutoFeedbackSurveyIndicator={false}
        showStaleIndicator
      />
    </Flex>
  );
}

function getTreeVisualizationTypesFromTableRow<TData>(
  row: Row<TData>,
  isGroupedRow: boolean = false,
): Array<IndentType> {
  // Don't show tree visualization for grouped rows
  if (isGroupedRow) {
    return [];
  }

  const parentRows = row.getParentRows().slice(1);

  const result: Array<IndentType> = parentRows.map((p) => {
    if (hasNextSibling(p)) {
      return "│";
    } else {
      return ".";
    }
  });

  // Handle current row
  if (row.getParentRow()) {
    result.push(hasNextSibling(row) ? "├" : "└");
  }

  // Render a tree root if the children are expanded
  if (row.getCanExpand() && row.getIsExpanded()) {
    result.push("▽");
  }

  return result;
}

function hasNextSibling<TData>(row: Row<TData>): boolean {
  const parentRow = row.getParentRow();

  if (!parentRow) {
    return false;
  }

  const siblings = parentRow.subRows;

  if (siblings.indexOf(row) < siblings.length - 1) {
    return true;
  } else {
    return false;
  }
}
